"use client";

import React, { ComponentPropsWithoutRef, FC, useEffect, useRef } from "react";

type InfiniteScrollProps = {
  loadMore: () => void;
  hasMore?: boolean;
  loader?: React.ReactNode;
  children: React.ReactNode;
  getScrollParent?: () => HTMLElement | null;
  useWindow?: boolean;
  initialLoad?: boolean;
} & ComponentPropsWithoutRef<"div">;

const InfiniteScroll: FC<InfiniteScrollProps> = ({
  loadMore,
  hasMore = false,
  loader,
  children,
  getScrollParent,
  useWindow = true,
  initialLoad = false,
  className,
  ...rest
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    // Get the scroll container
    const scrollContainer = getScrollParent
      ? getScrollParent()
      : useWindow
      ? null
      : sentinel.parentElement;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoadingRef.current) {
          isLoadingRef.current = true;
          loadMore();
          // Reset loading flag after a brief delay
          setTimeout(() => {
            isLoadingRef.current = false;
          }, 100);
        }
      },
      {
        root: scrollContainer,
        rootMargin: "500px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMore, getScrollParent, useWindow]);

  return (
    <div
      className={className}
      style={{ position: "relative", ...rest.style }}
      {...rest}
    >
      {children}
      {hasMore && loader}
      <div
        ref={sentinelRef}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 1,
          width: "100%",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default InfiniteScroll;
